import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";

interface MilitaryDetails {
  branch: string;
  startYear: string;
  endYear: string;
  dischargeStatus: string;
}

interface EmployerEntry {
  name: string;
  startYear: string;
  endYear: string;
}

export interface BenefitsTaskAnswers {
  military: MilitaryDetails | null;
  wasVeteran: boolean;
  employers: EmployerEntry[];
  isLoading: boolean;
}

const BENEFITS_TASK_IDS = ["employment", "military"];

function parseMilitaryDetails(raw: unknown): MilitaryDetails | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      branch: typeof parsed.branch === "string" ? parsed.branch : "",
      startYear: typeof parsed.startYear === "string" ? parsed.startYear : "",
      endYear: typeof parsed.endYear === "string" ? parsed.endYear : "",
      dischargeStatus: typeof parsed.dischargeStatus === "string" ? parsed.dischargeStatus : "",
    };
  } catch {
    return null;
  }
}

function parseEmployers(raw: unknown): EmployerEntry[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  try {
    return raw.map((s) => {
      const parsed =
        typeof s === "string"
          ? (JSON.parse(s) as Record<string, unknown>)
          : (s as Record<string, unknown>);
      return {
        name: typeof parsed.name === "string" ? parsed.name : "",
        startYear: typeof parsed.startYear === "string" ? parsed.startYear : "",
        endYear: typeof parsed.endYear === "string" ? parsed.endYear : "",
      };
    });
  } catch {
    return [];
  }
}

export function useBenefitsTaskAnswers(): BenefitsTaskAnswers {
  const { person } = useAppContext();
  const estateId = person?.estate_id;

  const { data: taskRows, isLoading } = useQuery({
    queryKey: ["benefitsTaskAnswers", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_tasks")
        .select("task_id, answers")
        .eq("estate_id", estateId!)
        .in("task_id", BENEFITS_TASK_IDS);

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const answers: Record<string, unknown> = {};

    if (taskRows) {
      for (const row of taskRows) {
        const rowAnswers = row.answers as Record<string, unknown> | null;
        if (rowAnswers) {
          for (const [key, value] of Object.entries(rowAnswers)) {
            answers[key] = value;
          }
        }
      }
    }

    const military = parseMilitaryDetails(answers.military_details);
    const wasVeteran = answers.military_service === "yes";
    const employers = parseEmployers(answers.employment_info);

    return {
      military,
      wasVeteran,
      employers,
      isLoading,
    };
  }, [taskRows, isLoading]);
}
